class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    /*
      The original version always used document.querySelector("[data-search-popup]"),
      which made every predictive-search instance depend on the full-screen popup.
      This version scopes itself to the closest popup if it is inside one,
      otherwise it scopes itself to the inline search component.
    */
    this.container = this.closest("[data-search-popup]") || this;

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");
    this.isInlineSearch = this.dataset.inlineSearch === "true";

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;

    if (!popularSearchItems || !popularSearchItems.length) return;

    popularSearchItems.forEach((itm) => {
      itm.href = createSearchLink(itm.dataset.psQuery);
    });
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");

    if (!form || !this.input) return;

    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener("focus", () => {
      if (this.getQuery().length && this.predictiveSearchResults && this.predictiveSearchResults.innerHTML.trim()) {
        this.open();
      }
    });

    document.addEventListener("click", (event) => {
      if (!this.isInlineSearch) return;
      if (this.contains(event.target)) return;

      this.close(false);
    });

    if (clear) {
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }

    if (window.MinimogEvents && window.MinimogTheme && MinimogTheme.pubSubEvents) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onClearSearch(event) {
    event.preventDefault();

    if (!this.input) return;

    this.input.value = "";
    this.input.focus();
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption || "last";

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(searchURL)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, "text/html");
        const resultsSection = parsed.querySelector("#shopify-section-predictive-search");

        if (!resultsSection) {
          this.close();
          return;
        }

        const resultsMarkup = resultsSection.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    const hasResults = searchItemsWrapper && searchItemsWrapper.childElementCount > 0;

    this.renderSearchQueryAndMessage(hasResults);
    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;

    if (!input || !searchQuery || !searchMessage || !this.predictiveSearchResults || !this.searchPopupSearults) return;

    const query = input.value;
    const { resultsTitle } = searchMessage.dataset;

    searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      if (moreResultIcon) moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      if (moreResultIcon) moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;

    if (submit) {
      submit.style.visibility = show ? "hidden" : "visible";
    }

    if (loading) {
      loading.style.visibility = show ? "visible" : "hidden";
    }
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;

    if (clear) {
      clear.style.visibility = show ? "visible" : "hidden";
    }
  }

  open() {
    if (!this.searchPopupSearults || !this.input) return;

    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.remove("m:hidden");
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");

    if (this.input) {
      this.input.setAttribute("aria-expanded", false);
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add("m:hidden");
    }

    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.add("m:hidden");
    }

    if (this.popularSearches) {
      this.popularSearches.classList.remove("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.add("m:hidden");
    }
  }

  getResultsMaxHeight() {
    const currentSearch = this;
    const bottom = currentSearch.getBoundingClientRect().bottom;
    const availableHeight = window.innerHeight - bottom - 12;

    this.resultsMaxHeight = Math.max(220, availableHeight);

    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);